import { useEffect, useRef, useState } from "react"
import { useUserQuery } from "@/api/users-api"
import { AppExperimentVariant } from "@/api/dto/users"
import { analytics, type ExperimentExposure } from "@/telemetry/amplitude"
import {
	CREATE_NEW_PROJECT_CARD_EXPERIMENT,
	getExperimentVariantOverride,
	subscribeExperimentVariantOverride,
} from "@/utils/measurement/experiment"

export function useCreateNewProjectCardExperiment() {
	const userQuery = useUserQuery(false)
	const experiment = CREATE_NEW_PROJECT_CARD_EXPERIMENT
	const [override, setOverride] = useState(() => getExperimentVariantOverride(experiment))
	const lastValid = useRef<{ userId: string; variant: AppExperimentVariant } | null>(null)
	const lastFailure = useRef<string | null>(null)
	const userId = userQuery.data?.userId
	const rawVariant = userQuery.data?.experiments?.[experiment.key]
	const validVariant =
		rawVariant === AppExperimentVariant.On || rawVariant === AppExperimentVariant.Off
			? rawVariant
			: null
	const backendVariant =
		validVariant ?? (lastValid.current?.userId === userId ? lastValid.current?.variant : null)
	const failure = userQuery.isError
		? "request_failure"
		: userQuery.isLoading
			? null
			: rawVariant == null
				? "missing_assignment"
				: validVariant === null
					? "invalid_assignment"
					: null

	useEffect(
		() =>
			subscribeExperimentVariantOverride(experiment, () => {
				setOverride(getExperimentVariantOverride(experiment))
			}),
		[experiment],
	)

	useEffect(() => {
		if (userId && validVariant !== null) {
			lastValid.current = { userId, variant: validVariant }
		} else if (lastValid.current?.userId !== userId) {
			lastValid.current = null
		}
	}, [userId, validVariant])

	useEffect(() => {
		if (override !== null || failure === null) {
			lastFailure.current = null
			return
		}
		const key = JSON.stringify([userId, failure])
		if (lastFailure.current === key) return
		lastFailure.current = key
		analytics.trackEvent({
			label: "experiment_assignment_failed",
			type: "error",
			customProperties: { experiment_key: experiment.key, failure_reason: failure },
		})
	}, [experiment.key, failure, override, userId])

	const variant = override ?? backendVariant
	const exposure: ExperimentExposure = {
		experimentKey: experiment.key,
		variant: variant ?? "FALLBACK",
		assignmentSource:
			override !== null
				? "local_override"
				: backendVariant != null
					? "backend"
					: (failure ?? "missing_assignment"),
	}
	return {
		exposure,
		userId,
		isLoading: userQuery.isLoading && variant == null,
		showCreateNewProjectCard: variant === AppExperimentVariant.On,
	}
}
